// Other Plugins
import { logManager } from '../../logs'
import BasicError, { callCodes } from '../../errors'
import { getOptions } from '../../call/interfaceNew/selectors'

// Helpers.
import { changeDtlsRoleTo } from '../utils'
import { hasSameSessionId } from '../utils/sdp'
import runPipeline from '../sdp/pipeline'

// Libraries.
import { call, select } from 'redux-saga/effects'

/**
 * Compares whether a new remote SDP for a Session has the same SDP session ID
 *    of the previous remote SDP.
 * @method isSameSdpSessionId
 * @param  {Object}  webRTC    The webRTC stack.
 * @param  {string}  sessionId ID of the Session under question.
 * @param  {string}  sdp       New remote SDP to compare the current one with.
 * @return {boolean}
 */
export function * isSameSdpSessionId (webRTC, sessionId, sdp) {
  const session = yield call([webRTC.sessionManager, 'get'], sessionId)
  const currentDesc = yield call([session, 'getRemoteDescription'])

  // TODO: How to act when there isn't a remote SDP set?
  // Currently: Return falsy to cause a recreate. If there isn't a remote SDP
  //    set, it's possible we're in an error scenario.
  if (currentDesc && currentDesc.sdp) {
    const sameId = yield call(hasSameSessionId, currentDesc.sdp, sdp)
    return sameId
  }
}

/**
 * An answer SDP has been received and should be applied to the Session.
 *
 * This saga is intended to handle all webRTC scenarios where a remote answer
 *    SDP has been received and needs to be added to the Session. At this point
 *    in negotiation, a Session should have been created and a local offer SDP
 *    set. This can be for initial negotiation or for midcall renegotiation, so
 *    the Session may or may not have a "current remote SDP" already.
 * This saga will complete the negotiation process, as both ends will have both
 *    an offer and answer SDP set.
 * Responsibilities:
 *    1. Ensure this is a valid operation.
 *        - Session exists, Session is in correct signaling state.
 *    2. Check the answer SDP for abnormal scenarios.
 *        - DTLS role issue.
 *    3. Process the answer SDP via Session's APIs.
 * @method receivedAnswer
 * @param {Object} webRTC An instance of webRTC
 * @param {Object} sessionInfo Session related information for the operation.
 * @param {string} sessionInfo.sessionId ID for the local webRTC Session.
 * @param {string} sessionInfo.answerSdp The received answer SDP.
 * @param {Object} targetCall Information about the call that this Session is associated with.
 * @returns {Object} Error object if any have occurred. Undefined otherwise.
 */
export function * receivedAnswer (deps, sessionInfo, targetCall) {
  const { webRTC } = deps
  const log = logManager.getLogger('CALL', targetCall.id)
  log.debug(`Processing SDP answer for session ${sessionInfo.sessionId}.`)

  const sessionId = sessionInfo.sessionId
  let answerSdp = sessionInfo.answerSdp
  const session = yield call([webRTC.sessionManager, 'get'], sessionId)

  if (!session) {
    log.debug(`webRTC session ${sessionId} not found.`)
    // TODO: Better error.
    return
  }
  // TODO: Ensure Session is in the correct signaling state for an answer SDP.

  const callConfigOptions = yield select(getOptions)
  let sdpHandlers = callConfigOptions.sdpHandlers
  /*
   * If the answer we received has DTLS role of 'actpass', then this is not a
   *    normal webRTC scenario. An answer SDP cannot have 'actpass' as the role.
   * It is assumed that the reason for this is a slow start call.
   */
  if (answerSdp.includes('a=setup:actpass')) {
    /*
     * Change the DTLS role depending on which side of the call the user is.
     * Change the caller to passive and the callee to active roles, because
     *    this is how it would be set for a regular call.
     */
    const newRole = targetCall.isCaller ? 'passive' : 'active'
    log.debug(`Received answer SDP has role of actpass. Changing to ${newRole}.`)
    sdpHandlers = [...sdpHandlers, changeDtlsRoleTo(newRole)]
  }

  /*
   * Provide the answer SDP to the Session for "processing".
   * If successful, the webRTC negotiation process is considered complete.
   */
  let error
  try {
    /*
     * Run the remote SDP answer through any SDP handlers provided, then set it
     *    as the Session's remote description.
     * This is the "pre set remote" stage.
     */
    answerSdp = yield call(runPipeline, sdpHandlers, answerSdp, {
      callId: targetCall.id,
      type: 'answer',
      step: 'set',
      endpoint: 'remote'
    })
    yield call([session, 'processAnswer'], {
      type: 'answer',
      sdp: answerSdp
    })
  } catch (err) {
    // TODO: Better error handling.
    log.debug(`Failed to process answer: ${err.message}`)
    let errorInfo
    if (err.message.includes('The order of m-lines')) {
      errorInfo = {
        code: callCodes.SESSION_MISMATCH,
        message: 'Failed to receive answer due to media negotiation mismatch.'
      }
    } else {
      errorInfo = {
        code: callCodes.GENERIC_ERROR,
        message: `Failed to receive answer: ${err.message}`
      }
    }
    error = new BasicError(errorInfo)
  }

  // TODO: Handle webRTC errors here.

  return error
}
